/**
 * Email Suppressions (Mar 2026) — admin side of the global unsubscribe
 * system. Lets the admin:
 *   1. Manually unsubscribe an address (for "please stop emailing me"
 *      replies) — adds it to the master do-not-email list enforced
 *      centrally in `_send_email`.
 *   2. See everyone on the suppression list + remove (re-subscribe).
 *   3. See owners who opted out of event-inquiry matching and re-enable.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import {
  Prohibit, ArrowsClockwise, Trash, CaretDown, EnvelopeSimple, CalendarX,
} from "@phosphor-icons/react";

function fmtWhen(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function EmailSuppressionsCard() {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suppressions, setSuppressions] = useState([]);
  const [eventOptouts, setEventOptouts] = useState([]);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyEmail, setBusyEmail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email/suppressions");
      setSuppressions(data.suppressions || []);
      setEventOptouts(data.event_optouts || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load suppression list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) load();
    // eslint-disable-next-line
  }, [expanded]);

  const addSuppression = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter a valid email address.");
      return;
    }
    setAdding(true);
    try {
      await api.post("/admin/email/suppressions", { email: trimmed, reason: "admin manual" });
      toast.success(`${trimmed} unsubscribed — they won't get any more emails.`);
      setEmail("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't unsubscribe that address");
    } finally {
      setAdding(false);
    }
  };

  const removeSuppression = async (addr) => {
    if (!window.confirm(`Re-subscribe ${addr}? They'll start receiving TruckTrackr emails again.`)) return;
    setBusyEmail(addr);
    try {
      await api.delete(`/admin/email/suppressions/${encodeURIComponent(addr)}`);
      toast.success(`${addr} re-subscribed.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't remove");
    } finally {
      setBusyEmail(null);
    }
  };

  const removeEventOptout = async (addr) => {
    if (!window.confirm(`Re-enable event requests for ${addr}?`)) return;
    setBusyEmail(addr);
    try {
      await api.delete(`/admin/email/event-optouts/${encodeURIComponent(addr)}`);
      toast.success(`${addr} will be matched with event inquiries again.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't re-enable");
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <section data-testid="admin-email-suppressions-card">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
            <Prohibit weight="fill" className="h-3.5 w-3.5" />
            Do-not-email list
          </div>
          <h2 className="font-display font-semibold text-xl tracking-tight mt-1">
            Unsubscribes
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-lg">
            Manually unsubscribe anyone who asks. Suppressed addresses are blocked from
            every marketing/nudge/campaign email at send time.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          data-testid="suppressions-toggle-btn"
          className="gap-1"
        >
          <CaretDown weight="bold" className={"h-3.5 w-3.5 transition-transform " + (expanded ? "rotate-180" : "")} />
          {expanded ? "Collapse" : "Manage"}
        </Button>
      </div>

      {expanded && (
        <div className="rounded-2xl border border-border bg-card p-4 space-y-5">
          {/* Add form */}
          <form onSubmit={addSuppression} className="flex flex-wrap gap-2 items-center">
            <Input
              type="email"
              placeholder="person@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="max-w-xs"
              data-testid="suppression-email-input"
            />
            <Button type="submit" size="sm" disabled={adding} data-testid="suppression-add-btn" className="gap-1">
              <Prohibit weight="bold" className="h-3.5 w-3.5" />
              {adding ? "Unsubscribing…" : "Unsubscribe"}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="suppression-refresh-btn">
              <ArrowsClockwise weight="bold" className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
            </Button>
          </form>

          {/* Suppression list */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1.5">
              <EnvelopeSimple weight="duotone" className="h-3.5 w-3.5" />
              Suppressed addresses ({suppressions.length})
            </div>
            {suppressions.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="suppressions-empty">
                Nobody is suppressed. Unsubscribe clicks and manual adds show up here.
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                {suppressions.map((s) => (
                  <li key={s.email} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`suppression-row-${s.email}`}>
                    <div className="min-w-0">
                      <span className="font-medium truncate">{s.email}</span>
                      <span className="text-[11px] text-muted-foreground ml-2">
                        {s.source === "admin" ? "added by admin" : "one-click unsubscribe"} · {fmtWhen(s.created_at)}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSuppression(s.email)}
                      disabled={busyEmail === s.email}
                      data-testid={`suppression-remove-${s.email}`}
                      className="gap-1 text-rose-600 hover:text-rose-700 flex-none"
                    >
                      <Trash weight="duotone" className="h-3.5 w-3.5" />
                      Re-subscribe
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Event-inquiry opt-outs */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1.5">
              <CalendarX weight="duotone" className="h-3.5 w-3.5" />
              Opted out of event requests ({eventOptouts.length})
            </div>
            {eventOptouts.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="event-optouts-empty">
                No owners have opted out of event-inquiry matching.
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                {eventOptouts.map((u) => (
                  <li key={u.email} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`event-optout-row-${u.email}`}>
                    <div className="min-w-0">
                      <span className="font-medium truncate">{u.email}</span>
                      {u.name && <span className="text-[11px] text-muted-foreground ml-2">{u.name}</span>}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEventOptout(u.email)}
                      disabled={busyEmail === u.email}
                      data-testid={`event-optout-remove-${u.email}`}
                      className="gap-1 flex-none"
                    >
                      <ArrowsClockwise weight="duotone" className="h-3.5 w-3.5" />
                      Re-enable
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
